/**
 * AI Gateway HITL review hooks (FND-010).
 *
 * Wraps the human-in-the-loop review surface — the single place a human
 * confirms low-confidence, advisory AI output:
 *   - GET  /api/v1/ai/review-tasks              → AIReviewTask[] (open + resolved)
 *   - POST /api/v1/ai/review-tasks/{id}:resolve → resolve with a human decision
 *
 * AI is advisory only; resolving a task records the reviewer's decision and
 * never commits a downstream action automatically.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { AIReviewTask, ReviewDecision } from '../api/types';

interface ReviewTaskListResponse {
  items: AIReviewTask[];
}

interface UseAiReviewResult {
  tasks: AIReviewTask[];
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  refresh: () => void;
  resolve: (
    taskId: string,
    decision: ReviewDecision,
    note?: string
  ) => Promise<AIReviewTask | null>;
}

/** Loads the tenant's HITL review queue and exposes the resolve action. */
export function useAiReview(): UseAiReviewResult {
  const [tasks, setTasks] = useState<AIReviewTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<ReviewTaskListResponse>('/ai/review-tasks')
      .then((res) => {
        setTasks(res.items ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setTasks([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resolve = useCallback(
    async (
      taskId: string,
      decision: ReviewDecision,
      note?: string
    ): Promise<AIReviewTask | null> => {
      setIsWorking(true);
      setError(null);
      try {
        const resolved = await apiClient.post<AIReviewTask>(
          `/ai/review-tasks/${encodeURIComponent(taskId)}:resolve`,
          { decision, note: note ?? null }
        );
        setTasks((prev) => prev.map((t) => (t.id === resolved.id ? resolved : t)));
        return resolved;
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    []
  );

  return { tasks, isLoading, isWorking, error, refresh, resolve };
}
